import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Member Detail Drawer - Slide-out panel for member details
 *
 * Features:
 * - Tabs: Overview, Meetings, Notes, Recordings
 * - Member stats and activity
 * - Role management (admin only)
 */

export default function MemberDetailDrawer({ member, organization, isAdmin, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [memberStats, setMemberStats] = useState({
    totalMeetings: 0,
    totalMinutes: 0,
    avgDuration: 0,
    guestsHosted: 0
  });
  const [memberMeetings, setMemberMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (member && organization?.id) {
      loadMemberData();
    }
  }, [member, organization?.id]);

  const loadMemberData = async () => {
    try {
      setLoading(true);

      // Query meetings hosted by this member
      const meetingsRef = collection(db, `organizations/${organization.id}/meetings`);
      const q = query(meetingsRef, where('createdBy', '==', member.userId || member.id));
      const snapshot = await getDocs(q);

      let totalMinutes = 0;
      let guestsHosted = 0;
      const meetings = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        meetings.push({ id: doc.id, ...data });
        if (data.duration) totalMinutes += data.duration;
        if (data.guestCount) guestsHosted += data.guestCount;
      });

      setMemberStats({
        totalMeetings: snapshot.size,
        totalMinutes,
        avgDuration: snapshot.size > 0 ? Math.round(totalMinutes / snapshot.size) : 0,
        guestsHosted
      });

      setMemberMeetings(meetings.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      }).slice(0, 10));

    } catch (error) {
      console.error('Error loading member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (newRole) => {
    if (!isAdmin || member.role === 'owner') return;

    try {
      const memberRef = doc(db, `organizations/${organization.id}/members/${member.id}`);
      await updateDoc(memberRef, { role: newRole });
      // Refresh will happen through realtime listener
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleRemoveMember = async () => {
    if (!isAdmin || member.role === 'owner') return;
    if (!confirm(`Remove ${member.email} from the organization?`)) return;

    try {
      const memberRef = doc(db, `organizations/${organization.id}/members/${member.id}`);
      await deleteDoc(memberRef);
      onClose();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'meetings', label: 'Meetings' },
    { id: 'notes', label: 'Notes' },
    { id: 'recordings', label: 'Recordings' }
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-[#1c1c1e] border-l border-white/10 z-50 overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold">Member Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Member Info */}
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center text-white font-bold text-xl">
              {member.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg">
                {member.displayName || member.email?.split('@')[0] || 'Unknown'}
              </h3>
              <p className="text-white/50 text-sm">{member.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                  member.role === 'owner' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                  member.role === 'admin' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                  'bg-white/10 text-white/70 border-white/20'
                }`}>
                  {member.role || 'member'}
                </span>
                <span className="text-white/40 text-xs">Joined {formatDate(member.joinedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 py-3 border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-white/20 border-t-[#06c] rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Meetings</div>
                      <div className="text-2xl font-bold text-white">{memberStats.totalMeetings}</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Total Time</div>
                      <div className="text-2xl font-bold text-white">{Math.floor(memberStats.totalMinutes / 60)}h</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Avg Duration</div>
                      <div className="text-2xl font-bold text-white">{memberStats.avgDuration}m</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Guests</div>
                      <div className="text-2xl font-bold text-white">{memberStats.guestsHosted}</div>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  {isAdmin && member.role !== 'owner' && (
                    <div className="space-y-4">
                      <h4 className="text-white/50 text-xs uppercase tracking-wider">Admin Actions</h4>

                      {/* Change Role */}
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <label className="block text-white/70 text-sm mb-2">Change Role</label>
                        <select
                          value={member.role || 'member'}
                          onChange={(e) => handleRoleChange(e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#06c]/50"
                        >
                          <option value="admin" className="bg-[#1c1c1e]">Admin</option>
                          <option value="member" className="bg-[#1c1c1e]">Member</option>
                        </select>
                      </div>

                      {/* Remove Member */}
                      <button
                        onClick={handleRemoveMember}
                        className="w-full px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-sm font-medium"
                      >
                        Remove from Organization
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Meetings Tab */}
              {activeTab === 'meetings' && (
                <div className="space-y-3">
                  {memberMeetings.length > 0 ? (
                    memberMeetings.map(meeting => (
                      <div key={meeting.id} className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <div className="text-white font-medium text-sm">{meeting.title || 'Untitled Meeting'}</div>
                        <div className="flex items-center gap-3 mt-2 text-white/50 text-xs">
                          <span>{formatDate(meeting.createdAt)}</span>
                          <span>{meeting.duration ? `${meeting.duration}m` : 'N/A'}</span>
                          <span>{meeting.participantCount || 0} participants</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-3xl mb-3">📅</div>
                      <div className="text-white/50 text-sm">No meetings hosted yet</div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="text-center py-12">
                  <div className="text-3xl mb-3">📝</div>
                  <div className="text-white/50 text-sm">Member notes coming soon</div>
                </div>
              )}

              {/* Recordings Tab */}
              {activeTab === 'recordings' && (
                <div className="text-center py-12">
                  <div className="text-3xl mb-3">🎥</div>
                  <div className="text-white/50 text-sm">Member recordings coming soon</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
